/**
 * User Access Page (Admin Only)
 *
 * Security Design:
 * - Admin can view user accounts, roles, activity, and status
 * - Admin may suspend/activate, assign role, soft-delete/restore (each action is audited server-side)
 * - No file contents or encryption keys are shown here
 * - Export to Excel for record keeping
 */

import React, { useState, useEffect, useMemo } from 'react';
import { getUsers, suspendUser, activateUser, assignUserRole, deleteUser, restoreUser } from '../services/api';
import { formatDateTime as tzFormatDateTime, formatDate as tzFormatDate, formatRelative as tzFormatRelative } from '../utils/time';
import * as XLSX from 'xlsx';

function UserAccessPage({ currentUserId }) {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [selectedUser, setSelectedUser] = useState(null);
  const [showDeleted, setShowDeleted] = useState(false);
  const [lastRefresh, setLastRefresh] = useState(new Date());
  const [actionLoading, setActionLoading] = useState(false);
  const [actionError, setActionError] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  const loadUsers = async () => {
    setLoading(true);
    try {
      const data = await getUsers(showDeleted);
      setUsers(data);
    } catch {
      setUsers([]);
    } finally {
      setLoading(false);
      setLastRefresh(new Date());
    }
  };

  useEffect(() => {
    loadUsers();
  }, [showDeleted]);

  // Use shared timezone-aware utilities
  const formatDate = (dateStr) => dateStr ? tzFormatDate(dateStr) : 'N/A';
  const formatDateTime = (dateStr) => dateStr ? tzFormatDateTime(dateStr) : 'Never';
  const getRelativeTime = (dateStr) => dateStr ? tzFormatRelative(dateStr) || 'Never' : 'Never';

  const getStatusStyle = (status, isSuspended, deletedAt) => {
    if (deletedAt) return { color: 'var(--text-muted)', bg: 'var(--bg-input)', border: 'var(--border)' };
    if (isSuspended) return { color: 'var(--red)', bg: 'rgba(239,68,68,0.15)', border: 'rgba(239,68,68,0.4)' };
    switch (status) {
      case 'active': return { color: 'var(--green)', bg: 'rgba(16,185,129,0.15)', border: 'rgba(16,185,129,0.4)' };
      case 'inactive': return { color: 'var(--text-muted)', bg: 'var(--bg-input)', border: 'var(--border)' };
      case 'flagged': return { color: 'var(--red)', bg: 'rgba(239,68,68,0.15)', border: 'rgba(239,68,68,0.4)' };
      default: return { color: 'var(--text-muted)', bg: 'var(--bg-input)', border: 'var(--border)' };
    }
  };

  const handleUserAction = async (fn, ...args) => {
    setActionError(null);
    setActionLoading(true);
    try {
      await fn(...args);
      await loadUsers();
      setSelectedUser(null);
    } catch (err) {
      setActionError(err.message || 'Action failed');
    } finally {
      setActionLoading(false);
    }
  };

  const filteredUsers = useMemo(() => {
    if (!search) return users;
    const q = search.toLowerCase();
    return users.filter(u =>
      (u.display_name || '').toLowerCase().includes(q) ||
      (u.email || '').toLowerCase().includes(q) ||
      (u.id || '').toLowerCase().includes(q) ||
      (u.role || '').toLowerCase().includes(q) ||
      (u.status || '').toLowerCase().includes(q)
    );
  }, [users, search]);

  const adminCount = users.filter(u => u.role === 'admin').length;
  const userCount = users.filter(u => u.role === 'user').length;
  const activeCount = users.filter(u => u.status === 'active').length;
  const flaggedCount = users.filter(u => u.status === 'flagged').length;
  const getDisplayStatus = (user) => {
    if (!user) return 'unknown';
    if (user.deleted_at) return 'deleted';
    if (user.is_suspended) return 'suspended';
    return user.status || 'unknown';
  };

  const exportToExcel = () => {
    const rows = users.map((u, i) => ({
      '#': i + 1,
      'Name': u.display_name || 'N/A',
      'Email': u.email,
      'Role': u.role,
      'Status': u.status || 'N/A',
      'Registered': formatDate(u.created_at),
      'Last Login': formatDateTime(u.last_login),
      'Files': u.files_count || 0,
      'Uploads': u.total_uploads || 0,
      'Decryptions': u.total_decryptions || 0,
      'Failed OTPs': u.failed_otps || 0,
      'Last IP': u.last_ip || 'N/A',
      'User ID': u.id,
    }));
    const ws = XLSX.utils.json_to_sheet(rows);
    ws['!cols'] = [
      { wch: 4 }, { wch: 20 }, { wch: 28 }, { wch: 8 }, { wch: 10 },
      { wch: 14 }, { wch: 22 }, { wch: 6 }, { wch: 8 }, { wch: 12 },
      { wch: 10 }, { wch: 16 }, { wch: 20 },
    ];
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Users');
    XLSX.writeFile(wb, `JOSMY_Users_${new Date().toISOString().split('T')[0]}.xlsx`);
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: 60 }}>
        <div style={{ width: 28, height: 28, borderWidth: 3, borderStyle: 'solid', borderColor: 'var(--border)', borderTopColor: 'var(--blue)', borderRadius: '50%', animation: 'spin 0.7s linear infinite' }} />
      </div>
    );
  }

  return (
    <div className="page-container admin-page-shell admin-tactile-page">
      {/* Header */}
      <div style={s.headerRow}>
        <div>
          <h2 style={s.pageTitle}>User Access Management</h2>
          <p style={s.subtitle}>
            View registered users and activity summaries. Account actions below are admin-controlled and audited; file data stays encrypted.
          </p>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <span style={{ fontSize: 11, color: 'var(--text-muted)' }} title="Browser local time when this list was loaded">
            Data fetched: {lastRefresh.toLocaleTimeString('en-IE')} (local)
          </span>
          <button onClick={loadUsers} style={s.refreshBtn} title="Refresh">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="23 4 23 10 17 10"/><path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"/>
            </svg>
          </button>
          <button onClick={exportToExcel} style={s.exportBtn}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <rect x="3" y="3" width="18" height="18" rx="2" ry="2"/>
              <line x1="3" y1="9" x2="21" y2="9"/><line x1="3" y1="15" x2="21" y2="15"/><line x1="9" y1="3" x2="9" y2="21"/>
            </svg>
            Export Excel
          </button>
        </div>
      </div>

      {/* Summary Cards */}
      <div style={s.summaryBar}>
        <SummaryCard label="Total Users" value={users.length} color="#6366F1" />
        <SummaryCard label="Admins" value={adminCount} color="var(--yellow)" />
        <SummaryCard label="Regular Users" value={userCount} color="#10B981" />
        <SummaryCard label="Active (7d)" value={activeCount} color="#0891b2" />
        {flaggedCount > 0 && <SummaryCard label="Flagged" value={flaggedCount} color="#EF4444" />}
      </div>

      {/* Show deleted + Search */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'center', marginBottom: 16 }}>
        <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 13, color: 'var(--text-secondary)' }}>
          <input type="checkbox" checked={showDeleted} onChange={(e) => setShowDeleted(e.target.checked)} />
          Show deleted users
        </label>
      </div>
      <div style={s.searchBar}>
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="var(--text-muted)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
        </svg>
        <input
          type="text" placeholder="Search by name, email, role, or status..."
          value={search} onChange={(e) => setSearch(e.target.value)} style={s.searchInput}
        />
        {search && (
          <button onClick={() => setSearch('')} style={s.searchClear}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
          </button>
        )}
      </div>

      {/* Users Table */}
      <div className="table-responsive" style={s.tableContainer}>
        <table style={s.table}>
          <thead>
            <tr>
              <th style={s.th}>User</th>
              <th style={s.th}>Role</th>
              <th style={s.th}>Status</th>
              <th style={s.th}>Files</th>
              <th style={s.th}>Last Login</th>
              <th style={s.th}>Registered</th>
              <th style={{ ...s.th, textAlign: 'center' }}>Details</th>
            </tr>
          </thead>
          <tbody>
            {filteredUsers.map((user) => {
              const statusStyle = getStatusStyle(user.status, user.is_suspended, user.deleted_at);
              const displayStatus = user.deleted_at ? 'deleted' : (user.is_suspended ? 'suspended' : (user.status || 'unknown'));
              return (
                <tr key={user.id}>
                  <td style={s.td}>
                    <div style={{ fontWeight: 500, color: 'var(--text-primary)', fontSize: 13 }}>{user.display_name || 'N/A'}</div>
                    <div style={{ color: 'var(--text-muted)', fontSize: 12 }}>{user.email}</div>
                  </td>
                  <td style={s.td}>
                    <span className={`security-badge ${user.role === 'admin' ? 'yellow' : 'green'}`}>{user.role}</span>
                  </td>
                  <td style={s.td}>
                    <span style={{
                      display: 'inline-block', padding: '3px 8px', borderRadius: 6,
                      fontSize: 12, fontWeight: 600, textTransform: 'uppercase',
                      backgroundColor: statusStyle.bg, color: statusStyle.color,
                      border: `1px solid ${statusStyle.border}`,
                    }}>
                      {displayStatus}
                    </span>
                  </td>
                  <td style={{ ...s.td, fontWeight: 600, color: 'var(--text-body)' }}>
                    {user.files_count || 0}
                  </td>
                  <td style={s.td}>
                    <div style={{ color: 'var(--text-body)', fontSize: 13 }}>{getRelativeTime(user.last_login)}</div>
                    <div style={{ color: 'var(--text-muted)', fontSize: 11 }}>{formatDateTime(user.last_login)}</div>
                  </td>
                  <td style={{ ...s.td, color: 'var(--text-muted)', fontSize: 12 }}>
                    {formatDate(user.created_at)}
                  </td>
                  <td style={{ ...s.td, textAlign: 'center' }}>
                    <button onClick={() => { setSelectedUser(user); setDeleteConfirm(null); }} style={s.detailBtn}>
                      View
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>

        {filteredUsers.length === 0 && (
          <div style={s.emptyState}>
            <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="var(--text-muted)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/>
            </svg>
            <p style={{ margin: '8px 0 0', color: 'var(--text-muted)', fontSize: 13 }}>
              {search ? 'No users matching your search.' : 'No users found.'}
            </p>
          </div>
        )}
      </div>

      {/* User Detail Modal */}
      {selectedUser && (
        <div style={s.modalOverlay} onClick={() => setSelectedUser(null)}>
          <div style={s.modal} onClick={(e) => e.stopPropagation()}>
            <div style={s.modalHeader}>
              <h3 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: 'var(--text-primary)' }}>User Details</h3>
              <button onClick={() => setSelectedUser(null)} style={s.modalClose}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
                </svg>
              </button>
            </div>

            {/* User Info */}
            <div style={s.modalSection}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 16 }}>
                <div style={s.avatar}>
                  {(selectedUser.display_name || selectedUser.email || '?')[0].toUpperCase()}
                </div>
                <div>
                  <div style={{ fontSize: 16, fontWeight: 600, color: 'var(--text-primary)' }}>{selectedUser.display_name || 'N/A'}</div>
                  <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>{selectedUser.email}</div>
                </div>
                <span className={`security-badge ${selectedUser.role === 'admin' ? 'yellow' : 'green'}`} style={{ marginLeft: 'auto' }}>
                  {selectedUser.role}
                </span>
              </div>

              {/* User Actions */}
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 16 }}>
                {selectedUser.id === currentUserId && (
                  <div style={{ fontSize: 12, color: 'var(--text-muted)', width: '100%' }}>
                    You are viewing your own admin account. Self-suspend and self-role-change are disabled.
                  </div>
                )}
                {selectedUser.deleted_at ? (
                  <button
                    onClick={() => handleUserAction(restoreUser, selectedUser.id)}
                    disabled={actionLoading}
                    style={{ ...s.actionBtn, backgroundColor: 'var(--green)', color: '#fff', border: 'none' }}
                  >
                    Restore user
                  </button>
                ) : (
                  <>
                    {selectedUser.is_suspended ? (
                      <button
                        onClick={() => handleUserAction(activateUser, selectedUser.id)}
                        disabled={actionLoading || selectedUser.id === currentUserId}
                        style={{ ...s.actionBtn, backgroundColor: 'var(--green)', color: '#fff', border: 'none' }}
                      >
                        Activate
                      </button>
                    ) : (
                      <button
                        onClick={() => handleUserAction(suspendUser, selectedUser.id)}
                        disabled={actionLoading || selectedUser.id === currentUserId}
                        style={{ ...s.actionBtn, backgroundColor: 'var(--red)', color: '#fff', border: 'none' }}
                      >
                        Suspend
                      </button>
                    )}
                    <button
                      onClick={() => handleUserAction(assignUserRole, selectedUser.id, 'user')}
                      disabled={actionLoading || selectedUser.role === 'user' || selectedUser.id === currentUserId}
                      style={s.actionBtn}
                    >
                      Role: User
                    </button>
                    <button
                      onClick={() => handleUserAction(assignUserRole, selectedUser.id, 'admin')}
                      disabled={actionLoading || selectedUser.role === 'admin' || selectedUser.id === currentUserId}
                      style={s.actionBtn}
                    >
                      Role: Admin
                    </button>
                    {currentUserId && selectedUser.id !== currentUserId && (
                      <button
                        onClick={() => setDeleteConfirm(selectedUser.id)}
                        disabled={actionLoading}
                        style={{ ...s.actionBtn, backgroundColor: 'var(--red)', color: '#fff', border: 'none' }}
                      >
                        Delete user
                      </button>
                    )}
                  </>
                )}
              </div>
              {deleteConfirm === selectedUser.id && (
                <div style={{ marginBottom: 16, padding: 12, background: 'rgba(239,68,68,0.08)', borderRadius: 8, border: '1px solid rgba(239,68,68,0.3)' }}>
                  <p style={{ margin: '0 0 10px', fontSize: 13, color: 'var(--text-primary)' }}>Remove this user? They will not be able to log in. You can restore them later.</p>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <button onClick={() => setDeleteConfirm(null)} style={s.actionBtn}>Cancel</button>
                    <button
                      onClick={async () => {
                        setDeleteConfirm(null);
                        await handleUserAction(deleteUser, selectedUser.id);
                      }}
                      disabled={actionLoading}
                      style={{ ...s.actionBtn, backgroundColor: 'var(--red)', color: '#fff', border: 'none' }}
                    >
                      Confirm delete
                    </button>
                  </div>
                </div>
              )}
              {actionError && (
                <div style={{ color: 'var(--red)', fontSize: 13, marginBottom: 12 }}>{actionError}</div>
              )}
            </div>

            {/* Detail Grid */}
            <div style={s.modalGrid}>
              <DetailBox label="User ID" value={selectedUser.id} mono />
              <DetailBox label="Status" value={getDisplayStatus(selectedUser)} />
              <DetailBox label="Registered" value={formatDateTime(selectedUser.created_at)} />
              <DetailBox label="Last Login" value={formatDateTime(selectedUser.last_login)} />
              <DetailBox label="Last IP" value={selectedUser.last_ip || 'N/A'} mono />
              <DetailBox label="Files Stored" value={selectedUser.files_count || 0} />
              <DetailBox label="Total Uploads" value={selectedUser.total_uploads || 0} />
              <DetailBox label="Total Decryptions" value={selectedUser.total_decryptions || 0} />
              <DetailBox label="Failed OTP Attempts" value={selectedUser.failed_otps || 0} highlight={selectedUser.failed_otps > 0} />
            </div>

            {/* Activity Summary */}
            <div style={s.modalActivity}>
              <div style={s.activityBar}>
                <div style={{ ...s.activitySeg, width: `${(selectedUser.total_uploads || 0) * 5}%`, backgroundColor: 'var(--blue)' }} />
                <div style={{ ...s.activitySeg, width: `${(selectedUser.total_decryptions || 0) * 5}%`, backgroundColor: '#8B5CF6' }} />
                <div style={{ ...s.activitySeg, width: `${(selectedUser.failed_otps || 0) * 5}%`, backgroundColor: 'var(--red)' }} />
              </div>
              <div style={{ display: 'flex', gap: 16, marginTop: 8 }}>
                <LegendDot color="var(--blue)" label={`${selectedUser.total_uploads || 0} uploads`} />
                <LegendDot color="#8B5CF6" label={`${selectedUser.total_decryptions || 0} decryptions`} />
                <LegendDot color="var(--red)" label={`${selectedUser.failed_otps || 0} failed OTPs`} />
              </div>
            </div>

            {selectedUser.failed_otps > 2 && (
              <div style={s.modalWarning}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="var(--red)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
                  <line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/>
                </svg>
                <span>This user has multiple failed OTP attempts. Check Security Alerts for details.</span>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Security Note */}
      <div style={s.securityNote}>
        <div style={s.securityNoteHeader}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="var(--text-secondary)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
          </svg>
          <strong>Admin Security Policy</strong>
        </div>
        <p style={{ margin: '4px 0 0' }}>
          Suspended users cannot login. Admin cannot access password or keys.
          All actions are audit-logged for accountability.
        </p>
      </div>
    </div>
  );
}

function SummaryCard({ label, value, color }) {
  return (
    <div style={s.summaryCard}>
      <div style={{ ...s.summaryIndicator, backgroundColor: color }} />
      <div>
        <div style={s.summaryNumber}>{value}</div>
        <div style={s.summaryLabel}>{label}</div>
      </div>
    </div>
  );
}

function DetailBox({ label, value, mono, highlight }) {
  return (
    <div style={{ padding: '10px 12px', backgroundColor: 'var(--bg-input)', borderRadius: 'var(--radius-sm)', border: '1px solid var(--border)' }}>
      <div style={{ fontSize: 11, color: 'var(--text-muted)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.3px', marginBottom: 4 }}>{label}</div>
      <div style={{ fontSize: 14, fontWeight: 600, color: highlight ? 'var(--red)' : 'var(--text-primary)', fontFamily: mono ? 'monospace' : 'inherit', wordBreak: 'break-all' }}>{value}</div>
    </div>
  );
}

function LegendDot({ color, label }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: 'var(--text-secondary)' }}>
      <div style={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: color, flexShrink: 0 }} />
      {label}
    </div>
  );
}

const s = {
  pageTitle: {
    fontSize: 22, fontWeight: 900, margin: '0 0 6px 0', letterSpacing: '-0.4px', lineHeight: 1.1,
    background: 'linear-gradient(135deg, #6366F1, #8B5CF6, #06B6D4)',
    WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text',
  },
  subtitle: { fontSize: 13, color: 'var(--text-muted)', margin: 0, lineHeight: 1.6, fontWeight: 500 },
  headerRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 32, gap: 16, flexWrap: 'wrap' },
  refreshBtn: {
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    padding: '10px 18px', borderRadius: 14,
    border: '1.5px solid rgba(99,102,241,0.12)', backgroundColor: 'var(--surface-85)',
    cursor: 'pointer', color: 'var(--text-muted)', fontSize: 14, fontWeight: 600,
    transition: 'all 0.3s ease', boxShadow: '0 2px 8px rgba(99,102,241,0.06)',
  },
  exportBtn: {
    display: 'flex', alignItems: 'center', gap: 6, padding: '10px 18px',
    borderRadius: 14, border: 'none',
    background: 'linear-gradient(135deg, #22C55E, #16A34A)', color: '#fff',
    fontSize: 14, fontWeight: 700, cursor: 'pointer',
    transition: 'all 0.3s cubic-bezier(0.22,1,0.36,1)',
    boxShadow: '0 3px 12px rgba(34,197,94,0.3)',
  },

  summaryBar: { display: 'flex', gap: 18, marginBottom: 32, flexWrap: 'wrap' },
  summaryCard: {
    backgroundColor: 'var(--surface-85)', padding: 24, borderRadius: 20,
    boxShadow: '0 4px 20px rgba(99,102,241,0.06)', border: '1.5px solid rgba(99,102,241,0.08)',
    display: 'flex', alignItems: 'center', gap: 14, minWidth: 140,
    backdropFilter: 'blur(12px)', transition: 'all 0.3s cubic-bezier(0.22,1,0.36,1)',
  },
  summaryIndicator: { width: 5, height: 40, borderRadius: 3, flexShrink: 0 },
  summaryNumber: { fontSize: 22, fontWeight: 900, color: 'var(--text-primary)', lineHeight: 1, letterSpacing: '-0.3px' },
  summaryLabel: { fontSize: 12, color: 'var(--text-muted)', marginTop: 3, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' },

  searchBar: {
    display: 'flex', alignItems: 'center', gap: 12,
    backgroundColor: 'var(--surface-90)', border: '1.5px solid rgba(99,102,241,0.1)',
    borderRadius: 16, padding: '0 16px', marginBottom: 32,
    boxShadow: '0 2px 8px rgba(99,102,241,0.04)', transition: 'all 0.2s ease',
  },
  searchInput: {
    flex: 1, border: 'none', outline: 'none', padding: '13px 0',
    fontSize: 13, color: 'var(--text-primary)', backgroundColor: 'transparent', fontFamily: 'inherit',
  },
  searchClear: { background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', display: 'flex', alignItems: 'center', padding: 4 },

  tableContainer: {
    backgroundColor: 'var(--surface-85)', borderRadius: 22,
    border: '1.5px solid rgba(99,102,241,0.08)', overflow: 'auto', marginBottom: 32,
    boxShadow: '0 4px 24px rgba(99,102,241,0.06)',
    backdropFilter: 'blur(12px)',
  },
  table: { width: '100%', borderCollapse: 'collapse', fontSize: 13 },
  th: {
    textAlign: 'left', padding: '14px 18px', fontSize: 11,
    background: 'linear-gradient(135deg, rgba(99,102,241,0.05), rgba(139,92,246,0.03))',
    fontWeight: 800, color: 'var(--text-muted)', borderBottom: '2px solid rgba(99,102,241,0.08)',
    whiteSpace: 'nowrap', textTransform: 'uppercase', letterSpacing: '0.8px',
  },
  td: { padding: '14px 18px', fontSize: 13, borderBottom: '1px solid rgba(99,102,241,0.05)', color: 'var(--text-secondary)' },
  emptyState: { textAlign: 'center', padding: '48px 24px' },
  detailBtn: {
    padding: '10px 18px', borderRadius: 14,
    border: 'none', background: 'linear-gradient(135deg, #6366F1, #8B5CF6)',
    color: '#fff', fontSize: 14, fontWeight: 700, cursor: 'pointer',
    transition: 'all 0.3s ease', boxShadow: '0 3px 10px rgba(99,102,241,0.25)',
  },

  // Modal
  modalOverlay: {
    position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
    backgroundColor: 'rgba(15,16,40,0.5)', display: 'flex',
    alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 24,
    backdropFilter: 'blur(6px)',
  },
  modal: {
    backgroundColor: 'var(--surface-95)', borderRadius: 24,
    maxWidth: 580, width: '100%', maxHeight: '85vh', overflowY: 'auto',
    padding: 28, boxShadow: '0 24px 80px rgba(99,102,241,0.18), 0 8px 24px rgba(0,0,0,0.08)',
    border: '1.5px solid rgba(99,102,241,0.1)',
    backdropFilter: 'blur(20px)',
  },
  modalHeader: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 18,
  },
  modalClose: {
    background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)',
    display: 'flex', alignItems: 'center', padding: 4, transition: 'all 0.2s ease',
  },
  modalSection: { marginBottom: 4 },
  actionBtn: {
    padding: '10px 18px', borderRadius: 14,
    border: '1.5px solid rgba(99,102,241,0.12)', backgroundColor: 'var(--surface-85)',
    color: 'var(--text-body)', fontSize: 14, fontWeight: 700, cursor: 'pointer',
    transition: 'all 0.2s ease',
  },
  avatar: {
    width: 52, height: 52, borderRadius: '50%',
    background: 'linear-gradient(135deg, #6366F1, #8B5CF6)',
    color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontSize: 22, fontWeight: 800, flexShrink: 0,
    boxShadow: '0 4px 12px rgba(99,102,241,0.25)',
  },
  modalGrid: {
    display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))',
    gap: 12, marginBottom: 18,
  },
  modalActivity: { marginBottom: 18 },
  activityBar: {
    display: 'flex', height: 12, borderRadius: 6, overflow: 'hidden',
    backgroundColor: 'rgba(99,102,241,0.04)',
  },
  activitySeg: { height: '100%' },
  modalWarning: {
    display: 'flex', alignItems: 'flex-start', gap: 10,
    backgroundColor: 'rgba(239,68,68,0.1)', padding: '12px 16px',
    borderRadius: 14, fontSize: 13, color: 'var(--red)',
    lineHeight: 1.5, border: '1.5px solid rgba(239,68,68,0.2)',
  },

  securityNote: {
    background: 'linear-gradient(135deg, rgba(99,102,241,0.06), rgba(139,92,246,0.03))',
    padding: '16px 20px', borderRadius: 18, fontSize: 13, color: 'var(--text-secondary)',
    lineHeight: 1.5, border: '1.5px solid rgba(99,102,241,0.1)',
  },
  securityNoteHeader: { display: 'flex', alignItems: 'center', gap: 10, fontSize: 14, color: 'var(--text-body)', fontWeight: 700 },
};

export default UserAccessPage;
